/**
 * InstallPrompt — invite l'utilisateur à installer Network-iA en PWA.
 *
 * Stratégie selon la plateforme :
 *  - iOS Safari : pas d'API native → tutoriel visuel "Partager → Ajouter à l'écran d'accueil"
 *  - Android Chrome : utilise `beforeinstallprompt` (prompt natif)
 *  - Desktop Chrome : idem Android Chrome
 *  - Autres : on cache
 *
 * Apparition : un bandeau bas-écran sur mobile + un modal explicatif au tap.
 * Persistance : si l'utilisateur ferme, on attend 7 jours avant de re-proposer.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Download, X, Share, Plus as PlusIcon, MoreVertical, Smartphone } from 'lucide-react';
import { IneaMark } from '@/components/IneaMark';
import { usePWAInstall } from '@/hooks/usePWAInstall';

export function InstallPrompt() {
  const { t } = useTranslation();
  const { platform, shouldShow, canPrompt, promptInstall, dismiss } = usePWAInstall();
  const [modalOpen, setModalOpen] = useState(false);

  if (!shouldShow) return null;

  async function handleInstall(): Promise<void> {
    if (canPrompt) {
      await promptInstall();
    } else {
      setModalOpen(true);
    }
  }

  return (
    <>
      {/* Bottom banner (mobile-first, also visible desktop) */}
      <AnimatePresence>
        {!modalOpen && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 380 }}
            className="fixed inset-x-3 bottom-[176px] z-40 mx-auto max-w-[440px] sm:bottom-[96px] sm:right-5 sm:left-auto sm:mx-0 sm:max-w-[360px]"
          >
            <div className="relative flex items-center gap-3 rounded-[22px] border border-white/70 bg-white p-3 pr-2 shadow-[0_22px_50px_-18px_rgba(168,139,250,0.40),0_8px_18px_-10px_rgba(236,72,153,0.20),inset_0_1px_0_rgba(255,255,255,0.95)]">
              <button
                onClick={dismiss}
                aria-label={t('common.close') as string}
                className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-ink/[0.06] text-ink/55 transition active:scale-95 active:bg-ink/[0.12]"
              >
                <X size={12} strokeWidth={2.4} />
              </button>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-blue via-violet to-pink p-0.5">
                <span className="flex h-full w-full items-center justify-center rounded-[14px] bg-white">
                  <IneaMark size={28} />
                </span>
              </span>
              <div className="flex-1 pr-6">
                <p className="font-display text-[13.5px] font-800 leading-tight tracking-[-0.01em] text-ink">
                  {t('pwa.bannerTitle')}
                </p>
                <p className="mt-0.5 text-[11.5px] leading-tight text-ink/55">
                  {t('pwa.bannerSubtitle')}
                </p>
              </div>
              <button
                onClick={handleInstall}
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gradient-to-r from-blue via-violet to-pink px-3 py-1.5 text-[12px] font-800 text-white shadow-[0_8px_18px_-8px_rgba(168,85,247,0.55)] transition active:scale-95"
              >
                <Download size={12} strokeWidth={2.4} />
                {t('pwa.install')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tutorial modal — iOS or desktop fallback */}
      <AnimatePresence>
        {modalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setModalOpen(false)}
              className="fixed inset-0 z-50 bg-night/55"
            />
            <motion.div
              initial={{ y: 40, opacity: 0, scale: 0.96 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 40, opacity: 0, scale: 0.96 }}
              transition={{ type: 'spring', damping: 30, stiffness: 380 }}
              role="dialog"
              aria-modal="true"
              className="fixed inset-x-3 bottom-4 z-50 mx-auto max-h-[88vh] max-w-[440px] overflow-y-auto rounded-[28px] border border-white/70 bg-white p-5 shadow-[0_-22px_60px_-12px_rgba(10,18,38,0.35)] sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:bottom-auto"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="kicker text-pink">
                    <Smartphone size={11} /> {t('pwa.modalKicker')}
                  </p>
                  <h2 className="mt-2 font-display text-[20px] font-800 leading-tight tracking-[-0.025em] text-ink">
                    {t('pwa.modalTitle')}
                  </h2>
                </div>
                <button
                  onClick={() => setModalOpen(false)}
                  aria-label={t('common.close') as string}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink/[0.06] text-ink/65 transition active:scale-95 active:bg-ink/[0.10]"
                >
                  <X size={15} strokeWidth={2.4} />
                </button>
              </div>

              <p className="mt-3 text-[13.5px] leading-[1.55] text-ink/65">
                {t('pwa.modalIntro')}
              </p>

              {platform === 'ios' ? <IOSTutorial /> : <AndroidTutorial />}

              <button
                onClick={() => {
                  dismiss();
                  setModalOpen(false);
                }}
                className="mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-ink/[0.10] bg-white px-4 py-2.5 text-[13px] font-700 text-ink/65 transition active:scale-[0.98] active:bg-ink/[0.04]"
              >
                {t('pwa.gotIt')}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function IOSTutorial() {
  const { t } = useTranslation();
  return (
    <ol className="mt-4 space-y-3">
      <Step
        n={1}
        icon={<Share size={18} strokeWidth={2.2} />}
        title={t('pwa.iosStep1Title')}
        text={t('pwa.iosStep1Text')}
        tint="#3b82f6"
      />
      <Step
        n={2}
        icon={<PlusIcon size={18} strokeWidth={2.4} />}
        title={t('pwa.iosStep2Title')}
        text={t('pwa.iosStep2Text')}
        tint="#a855f7"
      />
      <Step
        n={3}
        icon={<Download size={18} strokeWidth={2.4} />}
        title={t('pwa.iosStep3Title')}
        text={t('pwa.iosStep3Text')}
        tint="#ec4899"
      />
    </ol>
  );
}

function AndroidTutorial() {
  const { t } = useTranslation();
  return (
    <ol className="mt-4 space-y-3">
      <Step
        n={1}
        icon={<MoreVertical size={18} strokeWidth={2.2} />}
        title={t('pwa.androidStep1Title')}
        text={t('pwa.androidStep1Text')}
        tint="#3b82f6"
      />
      <Step
        n={2}
        icon={<Download size={18} strokeWidth={2.4} />}
        title={t('pwa.androidStep2Title')}
        text={t('pwa.androidStep2Text')}
        tint="#a855f7"
      />
      <Step
        n={3}
        icon={<Smartphone size={18} strokeWidth={2.4} />}
        title={t('pwa.androidStep3Title')}
        text={t('pwa.androidStep3Text')}
        tint="#ec4899"
      />
    </ol>
  );
}

function Step({
  n,
  icon,
  title,
  text,
  tint,
}: {
  n: number;
  icon: React.ReactNode;
  title: string;
  text: string;
  tint: string;
}) {
  return (
    <li className="flex items-start gap-3">
      <span
        className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white shadow-[0_8px_18px_-6px]"
        style={{
          background: `linear-gradient(135deg, ${tint}, ${tint}cc)`,
          boxShadow: `0 8px 18px -6px ${tint}`,
        }}
      >
        {icon}
        <span
          className="absolute -bottom-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white text-[10px] font-800"
          style={{ color: tint }}
        >
          {n}
        </span>
      </span>
      <div className="flex-1 pt-0.5">
        <p className="font-display text-[14px] font-800 leading-snug tracking-[-0.01em] text-ink">
          {title}
        </p>
        <p className="mt-0.5 text-[12.5px] leading-[1.5] text-ink/65">{text}</p>
      </div>
    </li>
  );
}
